/**
 * `<UIProvider>` is the root wrapper every app mounts once.
 *
 *   <UIProvider defaultColorScheme="auto">
 *     <App />
 *   </UIProvider>
 *
 * It does four things:
 *   1. Subscribes to the relevant slices of `useAppStore` from sdk-utils.
 *   2. Re-computes the CSS-variable map whenever those slices change.
 *   3. Writes the map onto the target element (default `<html>`) so every
 *      `--rr-*` token is live for the entire tree.
 *   4. Provides a `UIContext` so child components can read resolved values
 *      (resolved color scheme, primary color, density, …) for non-CSS branches.
 *
 * It also installs the focus-visible class once, mounts an aria-live announcer,
 * and synchronises a `[data-theme]` attribute for dark-mode CSS overrides.
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { installScrollReveal } from './scroll-reveal.js';
import { useAppStore } from '@repletoryreach/sdk-utils/store';
import {
  installFocusVisibleClass,
  createAnnouncer,
  prefersReducedMotionOs
} from '@repletoryreach/sdk-utils/a11y';
import { UIContext, type UIContextValue } from './use-ui-context.js';
import { computeCssVars, diffApplyCssVars } from './css-vars.js';
import './normalize.css';

export interface UIProviderProps {
  children: ReactNode;
  /**
   * Default color scheme when the app starts. Persists to `useAppStore.themeMode`.
   *  - `'auto'`: respects OS preference
   *  - `'light' | 'dark'`: forced
   *  - omitted: uses whatever is already in the store
   */
  defaultColorScheme?: 'auto' | 'light' | 'dark';
  /** CSS selector for the element CSS vars are written to. Default `:root`. */
  cssVarsSelector?: string;
  /** Optionally pass the target element directly (escape hatch for shadow-roots). */
  target?: HTMLElement;
  /** Disable the focus-visible class installer (rare). */
  withFocusVisible?: boolean;
  /** Disable the aria-live announcer (rare). */
  withAnnouncer?: boolean;
}

/** True once the person's theme has been saved in this browser: their choice, which a default never overrides. */
export function hasStoredThemeChoice(): boolean {
  try {
    const name = useAppStore.persist?.getOptions().name;
    const raw = name && typeof window !== 'undefined' ? window.localStorage.getItem(name) : null;
    if (!raw) return false;
    const mode = (JSON.parse(raw) as { state?: { themeMode?: unknown } }).state?.themeMode;
    return mode === 'light' || mode === 'dark' || mode === 'system';
  } catch {
    // Storage blocked or unreadable: nothing was saved, so the default applies.
    return false;
  }
}

function resolveScheme(mode: 'light' | 'dark' | 'system', prefersDark: boolean): 'light' | 'dark' {
  if (mode === 'system') return prefersDark ? 'dark' : 'light';
  return mode;
}

function usePrefersDark(): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return matches;
}

function useOsReducedMotion(): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined') return false;
    return prefersReducedMotionOs();
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return matches;
}

export function UIProvider({
  children,
  defaultColorScheme,
  cssVarsSelector = ':root',
  target,
  withFocusVisible = true,
  withAnnouncer = true
}: UIProviderProps): ReactNode {
  // Apply optional default exactly once.
  const appliedDefaultRef = useRef(false);
  // Native scrollbars hide at rest and show while a container scrolls; the
  // stylesheet in normalize.css reads the class this sets.
  useEffect(() => installScrollReveal(), []);

  useEffect(() => {
    if (appliedDefaultRef.current || !defaultColorScheme) return;
    appliedDefaultRef.current = true;
    // A default is for a first visit only. It used to be applied on every load, so a person who chose light was put
    // back on their computer's scheme (dark, for the owner) each time they came back (15 October 2026).
    if (hasStoredThemeChoice()) return;
    useAppStore.getState().setThemeMode(defaultColorScheme === 'auto' ? 'system' : defaultColorScheme);
  }, [defaultColorScheme]);

  // Subscribe to every slice that influences --rr-* vars.
  const themeMode = useAppStore((s) => s.themeMode);
  const primary = useAppStore((s) => s.primary);
  const secondary = useAppStore((s) => s.secondary);
  const tertiary = useAppStore((s) => s.tertiary);
  const textScale = useAppStore((s) => s.textScale);
  const density = useAppStore((s) => s.density);
  const reducedMotion = useAppStore((s) => s.reducedMotion);
  const highContrast = useAppStore((s) => s.highContrast);

  const prefersDark = usePrefersDark();
  const reducedMotionOs = useOsReducedMotion();
  const resolvedColorScheme = resolveScheme(themeMode, prefersDark);
  const reducedMotionResolved =
    reducedMotion === 'always' || (reducedMotion === 'auto' && reducedMotionOs);

  // Compute the var map.
  const computed = useMemo(
    () =>
      computeCssVars({
        primary,
        secondary,
        tertiary,
        textScale,
        density,
        reducedMotion,
        reducedMotionOs,
        highContrast
      }),
    [primary, secondary, tertiary, textScale, density, reducedMotion, reducedMotionOs, highContrast]
  );

  // Write CSS vars (diff against previous to avoid layout thrash).
  const prevVarsRef = useRef<Record<string, string> | null>(null);
  useEffect(() => {
    const el = target ?? (typeof document !== 'undefined' ? document.querySelector(cssVarsSelector) as HTMLElement | null : null);
    if (!el) return;
    diffApplyCssVars(el, computed.vars, prevVarsRef.current);
    prevVarsRef.current = computed.vars;
    el.setAttribute('data-theme', resolvedColorScheme);
    el.setAttribute('data-density', density);
    el.setAttribute('data-text-scale', textScale);
    el.setAttribute('data-reduce-motion', reducedMotion);
    el.setAttribute('data-high-contrast', String(highContrast));
  }, [computed.vars, cssVarsSelector, target, resolvedColorScheme, density, textScale, reducedMotion, highContrast]);

  // Install focus-visible once.
  useEffect(() => {
    if (!withFocusVisible) return;
    return installFocusVisibleClass();
  }, [withFocusVisible]);

  // Mount aria-live announcer once.
  useEffect(() => {
    if (!withAnnouncer) return;
    const a = createAnnouncer();
    return () => a.destroy();
  }, [withAnnouncer]);

  const value = useMemo<UIContextValue>(
    () => ({
      resolvedColorScheme,
      colorScheme: themeMode,
      primary,
      secondary,
      tertiary,
      textScale,
      density,
      reducedMotion,
      reducedMotionResolved,
      highContrast,
      cssVarsSelector
    }),
    [
      resolvedColorScheme,
      themeMode,
      primary,
      secondary,
      tertiary,
      textScale,
      density,
      reducedMotion,
      reducedMotionResolved,
      highContrast,
      cssVarsSelector
    ]
  );

  return <UIContext.Provider value={value}>{children}</UIContext.Provider>;
}
